/**
 * Free gift promotion engine (3d_printed US).
 *
 * Contract (settings live in the "Free gift promotion" theme settings group,
 * serialised by snippets/cart-free-gift-data.liquid):
 *
 *   subtotal  = sum of final_line_price over the lines that are NOT gifts, so a
 *               gift can never pay for itself and the fixpoint always converges.
 *   priority  = 4+ Halloween Gifts units -> buy 3 get 1, remove all tier gifts
 *   tier 0    (< tier 1)          -> no gift, any leftover gift line is removed
 *   tier 1    (>= t1 and < t2)    -> gift A x1, falling back to gift B x1
 *   tier 2    (>= t2)             -> gift A x1 + gift B x1, falling back to the
 *                                    available one x2, or nothing when both are out
 *
 * Gift lines are marked with the private `_free_gift` line item property, which
 * keeps them separate from a paid line for the same variant and hides the marker
 * from the customer.
 *
 * Every write is planned against a server cart and only emitted when the cart
 * actually differs from the target, so the engine cannot ping-pong. All writes
 * are chained onto the single-flight lane published by assets/custom.js: the Cart
 * AJAX API builds each response from the cart as it was when the request started,
 * so a gift write overlapping a shopper's quantity change would silently drop one
 * of them.
 *
 * Because that lane is serial, every round trip is one the shopper waits through,
 * so a pass costs as few as the API allows: both gifts go out in one
 * /cart/add.js, the drawer section is rendered by the write itself (`sections`)
 * instead of by a follow-up GET ?section_id=, and /cart.js is only read when the
 * pass added something — /cart/change.js already answers with the whole cart.
 * What remains is covered by the skeleton row below, painted on the click.
 */
(() => {
 const dataNode = document.getElementById('cart-free-gift-data');

 if (!dataNode) {
  return;
 }

 let config = null;

 try {
  config = JSON.parse(dataNode.textContent);
 } catch (error) {
  console.error('Free gift: config is not valid JSON.', error);

  return;
 }

 if (!config?.enabled || !Array.isArray(config.gifts) || config.gifts.length === 0) {
  return;
 }

 const GIFT_PROPERTY = config.property || '_free_gift';
 const GIFT_PROPERTY_VALUE = 'true';
 // One pass per re-plan. Two are enough for the worst legitimate case (a sold-out
 // fallback discovered mid-flight); the third only exists so a cart the engine
 // cannot satisfy costs a bounded number of requests instead of a loop.
 const MAX_PASSES = 3;

 const [tier1Cents, tier2Cents] = Array.isArray(config.tiers) ? config.tiers : [0, 0];
 const giftA = config.gifts[0] || null;
 const giftB = config.gifts[1] || null;
 const routeRoot = window.Shopify?.routes?.root || '/';
 const priorityProductIds = new Set((config.priorityPromotion?.productIds || []).map(String));
 const priorityMinimumQuantity = config.priorityPromotion?.minimumQuantity || 4;

 // Variants that were available when the page rendered can still fail the add
 // (someone bought the last one). A rejected add parks the variant here so the
 // next plan falls back instead of retrying a dead variant forever.
 const soldOut = new Set(config.gifts.filter((gift) => gift.available === false).map((gift) => gift.id));

 const isCartPage = () => window.themeVariables?.settings?.pageType === 'cart';
 const inStock = (gift) => (gift && !soldOut.has(gift.id) ? gift : null);

 const planFor = (subtotalCents, priorityQuantity = 0) => {
  const plan = new Map();

  // Decide from eligible quantity, not current discount allocations: a tier gift
  // can displace BXGY on the server, but must still be removed to restore BXGY.
  if (priorityQuantity >= priorityMinimumQuantity) {
   return plan;
  }

  const available1 = inStock(giftA);
  const available2 = inStock(giftB);

  if (tier2Cents > 0 && subtotalCents >= tier2Cents) {
   if (available1 && available2) {
    plan.set(available1.id, 1);
    plan.set(available2.id, 1);
   } else if (available1 || available2) {
    plan.set((available1 || available2).id, 2);
   }

   return plan;
  }

  if (tier1Cents > 0 && subtotalCents >= tier1Cents && (available1 || available2)) {
   plan.set((available1 || available2).id, 1);
  }

  return plan;
 };

 // Accepts both the Cart AJAX API shape and the Liquid seed, which flags gift
 // lines directly instead of carrying the whole properties object.
 const readCart = (cart) => {
  const lines = Array.isArray(cart?.items) ? cart.items : [];
  const gifts = new Map();
  let subtotalCents = 0;
  let priorityQuantity = 0;

  lines.forEach((line) => {
   if (line.gift !== true && !line.properties?.[GIFT_PROPERTY]) {
    subtotalCents += line.final_line_price || 0;
    if (priorityProductIds.has(String(line.product_id))) {
     priorityQuantity += line.quantity || 0;
    }

    return;
   }

   const existing = gifts.get(line.variant_id) || [];

   existing.push({ key: line.key, quantity: line.quantity });
   gifts.set(line.variant_id, existing);
  });

  return { subtotalCents, priorityQuantity, gifts };
 };

 const signatureOf = ({ subtotalCents, priorityQuantity, gifts }) =>
  [
   subtotalCents,
   priorityQuantity,
   [...gifts.entries()]
    .map(([variantId, lines]) => `${variantId}:${lines.map((line) => line.quantity).join('.')}`)
    .sort()
    .join(',')
  ].join('|');

 // Duplicate gift lines for one variant (two adds racing, a stale cart restored
 // from bfcache) are collapsed onto the first line so the shopper never sees the
 // same gift twice.
 const buildOperations = (gifts, plan) => {
  const operations = [];

  gifts.forEach((lines, variantId) => {
   const target = plan.get(variantId) || 0;

   lines.forEach((line, index) => {
    const quantity = index === 0 ? target : 0;

    if (quantity !== line.quantity) {
     operations.push({ type: 'change', id: line.key, quantity });
    }
   });
  });

  plan.forEach((quantity, variantId) => {
   if (!gifts.has(variantId)) {
    operations.push({ type: 'add', id: variantId, quantity });
   }
  });

  return operations;
 };

 /* ------------------------------------------------------------------------
    Optimistic feedback.

    A tier crossing is several round trips away from being painted (the
    shopper's own quantity write, the gift write, the authoritative cart), and
    the drawer used to show nothing at all in between — the gifts simply
    appeared, which reads as a stall. The skeleton row serialised by
    snippets/cart-free-gift-data.liquid is therefore dropped in on the click
    itself, from the optimistic subtotal assets/custom.js publishes, and the row
    a falling subtotal is about to lose is dimmed the same way.

    Nothing here writes to the cart: eligibility stays the server's call, so a
    mis-predicted skeleton costs a placeholder that disappears again, never a
    gift the cart did not earn. The row carries no price either — the discounted
    total is the server's to state.
    ------------------------------------------------------------------------ */
 const PENDING_ROW_SELECTOR = 'line-item[data-free-gift="pending"]';
 const LIVE_ROW_SELECTOR = 'line-item[data-free-gift="true"]';
 const LEAVING_CLASS = 'cart-drawer-line-item--gift-leaving';
 // A preview only ever stands in for requests that are already on their way, so
 // it must not be able to outlive them however badly they end.
 const PREVIEW_TTL_MS = 8000;

 let previewing = false;
 let previewTimer = null;
 let previewSubtotalCents = null;

 const openDrawer = () => {
  const drawer = document.querySelector('cart-drawer');

  return drawer?.hasAttribute('open') ? drawer : null;
 };

 const variantIdOf = (row) =>
  row.getAttribute('data-free-gift-variant') || String(row.getAttribute('data-line-key') || '').split(':')[0];

 const dropPreview = ({ announce = false } = {}) => {
  clearTimeout(previewTimer);
  previewTimer = null;

  const wasPreviewing = previewing;
  previewing = false;

  document.querySelectorAll(PENDING_ROW_SELECTOR).forEach((row) => row.remove());
  document.querySelectorAll(`.${LEAVING_CLASS}`).forEach((row) => {
   row.classList.remove(LEAVING_CLASS);
   row.removeAttribute('aria-busy');
  });

  // The prediction did not materialise, so the drawer is not going to be
  // rebuilt: assets/custom.js skipped one cross-sell reload on the strength of
  // this preview and has to be told to make it up.
  if (wasPreviewing && announce) {
   document.documentElement.dispatchEvent(new CustomEvent('cart:gift-preview-dropped', { bubbles: true }));
  }
 };

 // The skeleton row is all shimmer, so nothing it paints needs the gift image —
 // but the real row the server renders right after it does, with loading="lazy"
 // and no head start. The drawer opening is always well before a tier is crossed
 // and is idle time, so the candidate `sizes="96px"` will pick is fetched there
 // instead and the server's row paints its media from cache. Candidates live on
 // the <template> element (snippets/cart-free-gift-data.liquid) so reading them
 // costs no clone.
 let warmedImages = false;

 const warmGiftImages = () => {
  if (warmedImages) {
   return;
  }

  warmedImages = true;

  document.querySelectorAll('template[data-free-gift-template]').forEach((template) => {
   const src = template.getAttribute('data-free-gift-image-src');

   if (!src) {
    return;
   }

   const warm = new Image();

   warm.sizes = template.getAttribute('data-free-gift-image-sizes') || '';
   warm.srcset = template.getAttribute('data-free-gift-image-srcset') || '';
   warm.src = src;
  });
 };

 const drawerPriorityQuantity = () =>
  [...(openDrawer()?.querySelectorAll('line-item[data-product-id]') || [])].reduce(
   (sum, row) =>
    priorityProductIds.has(row.getAttribute('data-product-id')) && !row.hasAttribute('data-free-gift')
     ? sum + (Number(row.querySelector('input[data-line-key]')?.value) || 0)
     : sum,
   0
  );

 let previewPriorityQuantity = 0;

 const paintPreview = (subtotalCents, priorityQuantity = drawerPriorityQuantity()) => {
  if (!Number.isFinite(subtotalCents)) {
   return;
  }

  previewSubtotalCents = subtotalCents;
  previewPriorityQuantity = priorityQuantity;

  const items = openDrawer()?.querySelector('.cart-drawer__items');

  if (!items) {
   return;
  }

  const wanted = new Set([...planFor(subtotalCents, priorityQuantity).keys()].map(String));
  const live = [...items.querySelectorAll(LIVE_ROW_SELECTOR)];
  const shown = new Set(live.map(variantIdOf));

  live.forEach((row) => {
   const leaving = !wanted.has(variantIdOf(row));

   row.classList.toggle(LEAVING_CLASS, leaving);
   row.setAttribute('aria-busy', String(leaving));
  });

  // A second click inside the same window must not stack a second skeleton for
  // the same gift, and one the subtotal no longer qualifies for has to go.
  items.querySelectorAll(PENDING_ROW_SELECTOR).forEach((row) => {
   const variantId = variantIdOf(row);

   if (wanted.has(variantId) && !shown.has(variantId)) {
    shown.add(variantId);

    return;
   }

   row.remove();
  });

  // A gift row lands at the TOP of the drawer, not the bottom: /cart/add.js
  // prepends every new line (probed on nexbie.com — adding [B, C] in one call to
  // a cart already holding A answers with C, B, A), so a gift written now
  // outranks both the shopper's lines and a gift line an earlier tier added.
  // Prepending in plan order reproduces that insertion exactly — each skeleton
  // pushes the previous one down the same way the cart will, and the batch add
  // that follows reverses its payload the same way — so the placeholder occupies
  // the pixels the server's row is about to take and the swap is motionless.
  [...wanted]
   .filter((variantId) => !shown.has(variantId))
   .forEach((variantId) => {
    const template = document.querySelector(`template[data-free-gift-template="${variantId}"]`);

    if (template) {
     items.prepend(template.content.cloneNode(true));
     shown.add(variantId);
    }
   });

  previewing = items.querySelector(`${PENDING_ROW_SELECTOR}, .${LEAVING_CLASS}`) !== null;
  clearTimeout(previewTimer);
  previewTimer = previewing ? setTimeout(() => dropPreview({ announce: true }), PREVIEW_TTL_MS) : null;
 };

 // Every write here is the engine's, never the shopper's: a gift is awarded by
 // the cart crossing a threshold, not chosen. Shopify's pixel manager cannot
 // tell the two apart and would report each gift as an add-to-cart at its full
 // price (and its withdrawal as a removal), inflating both the count and the
 // value the ad platforms optimise on. assets/custom.js publishes a lane that
 // reaches the same cart unobserved; without it the writes still go through,
 // reported as before, because the gift must never depend on that lane existing.
 const postJson = async (path, body) => {
  const init = {
   method: 'POST',
   headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
   body: JSON.stringify(body)
  };
  const silentFetch = window.NexbieCart?.silentFetch;
  const response = silentFetch ? await silentFetch(path, init) : await fetch(`${routeRoot}${path}`, init);
  const payload = await response.json().catch(() => null);

  return { ok: response.ok, status: response.status, payload };
 };

 const fetchCart = async () => {
  const response = await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' } });

  return response.json();
 };

 // Have the storefront render the drawer inside the write itself. theme.js's
 // CartDrawer takes its markup from detail.cart.sections when the event carries
 // it and only falls back to GET ?section_id= otherwise, which removes the last
 // round trip between the write landing and the gift being on screen. Only worth
 // asking for while the drawer is open: a section render is not free server-side
 // and a closed drawer has nobody waiting on it.
 const bundledSections = () => {
  if (!openDrawer()) {
   return null;
  }

  const sections = [];

  document.documentElement.dispatchEvent(
   new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
  );

  return sections.length > 0 ? sections.join(',') : null;
 };

 const withSections = (body, sections) => (sections ? { ...body, sections } : body);

 const addBody = (adds) => ({
  items: adds.map((add) => ({
   id: add.id,
   quantity: add.quantity,
   properties: { [GIFT_PROPERTY]: GIFT_PROPERTY_VALUE }
  }))
 });

 const runAdds = async (adds, sections) => {
  if (adds.length > 1) {
   const batched = await postJson('cart/add.js', withSections(addBody(adds), sections));

   if (batched.ok) {
    return { ok: true, sections: batched.payload?.sections || null };
   }

   // A rejected batch does not say which variant Shopify refused, and retiring
   // both would drop a gift that is still in stock — so the retry goes one at a
   // time until the rejection lands on the variant that earned it. A variant the
   // batch did add before failing comes back as a duplicate gift line, which
   // buildOperations collapses on the next pass.
  }

  let rendered = null;

  for (const [index, add] of adds.entries()) {
   const single = await postJson(
    'cart/add.js',
    withSections(addBody([add]), index === adds.length - 1 ? sections : null)
   );

   if (!single.ok) {
    // 422 is Shopify's "not enough items available"; anything else is treated the
    // same way so a variant that cannot be added stops blocking the fallback.
    soldOut.add(add.id);

    return { ok: false, sections: rendered };
   }

   rendered = single.payload?.sections || rendered;
  }

  return { ok: true, sections: rendered };
 };

 // One planning pass. Removals go first — a gift that lost its tier should not
 // outlive it by a round trip — and each returns the whole cart, so a
 // removal-only pass needs no extra read. /cart/add.js answers with the added
 // line items instead, so `cart: null` tells the caller to read one.
 const applyPass = async (operations) => {
  const changes = operations.filter((operation) => operation.type === 'change');
  const adds = operations.filter((operation) => operation.type === 'add');
  const sections = bundledSections();
  let cart = null;
  let rendered = null;

  for (const [index, change] of changes.entries()) {
   const isLast = adds.length === 0 && index === changes.length - 1;
   const { ok, payload } = await postJson(
    'cart/change.js',
    withSections({ id: change.id, quantity: change.quantity }, isLast ? sections : null)
   );

   if (!ok) {
    return { cart: null, sections: null };
   }

   cart = payload || cart;
   rendered = payload?.sections || rendered;
  }

  if (adds.length === 0) {
   return { cart, sections: rendered };
  }

  const added = await runAdds(adds, sections);

  return { cart: null, sections: added.sections };
 };

 const settle = async (seedCart) => {
  let cart = seedCart;
  let mutated = false;
  // Only the last pass's markup may be published: it has to describe the very
  // cart that goes out with it, and an earlier pass's HTML no longer does.
  let sections = null;

  for (let pass = 0; pass <= MAX_PASSES; pass += 1) {
   const state = readCart(cart);
   const operations = buildOperations(state.gifts, planFor(state.subtotalCents, state.priorityQuantity));

   if (operations.length === 0) {
    return { mutated, cart, sections, gaveUp: false };
   }

   if (pass === MAX_PASSES) {
    console.warn(`Free gift: cart still off target after ${MAX_PASSES} passes, leaving it alone.`);

    return { mutated, cart, sections, gaveUp: true };
   }

   const result = await applyPass(operations);

   mutated = true;
   sections = result.sections;
   cart = result.cart || (await fetchCart());
  }

  return { mutated, cart, sections, gaveUp: true };
 };

 const publish = (cart, sections) => {
  if (isCartPage()) {
   window.location.reload();

   return;
  }

  // `sections` is the drawer markup the write already rendered: CartDrawer
  // replaces its content from detail.cart.sections and only falls back to
  // ?section_id= when the event carries none, which is still the right
  // behaviour for a closed drawer. baseEvent is not "variant:add", so the
  // drawer is never force-opened by a gift sync.
  document.documentElement.dispatchEvent(
   new CustomEvent('cart:change', {
    bubbles: true,
    detail: { baseEvent: 'line-item:change', cart: sections ? { ...cart, sections } : cart }
   })
  );
 };

 let syncing = false;
 // Cart state the engine already failed to satisfy, so a cart it cannot fix does
 // not re-issue the same doomed requests on every subsequent cart event.
 let blockedSignature = null;

 const runExclusive = (task) =>
  typeof window.NexbieCart?.enqueueMutation === 'function' ? window.NexbieCart.enqueueMutation(task) : task();

 const sync = (cart) => {
  if (syncing || !cart || window.NexbieCart?.hasPendingUserChange?.() === true) {
   return;
  }

  const state = readCart(cart);
  const signature = signatureOf(state);

  if (signature === blockedSignature) {
   dropPreview({ announce: true });

   return;
  }

  // The authoritative subtotal: corrects a skeleton the optimistic pass could
  // only estimate, and is the only feedback the PDP add-to-cart path gets.
  paintPreview(state.subtotalCents, state.priorityQuantity);

  syncing = true;

  return runExclusive(async () => {
   let published = false;

   try {
    const result = await settle(cart);

    blockedSignature = result.gaveUp ? signature : null;

    if (result.mutated && !result.gaveUp) {
     publish(result.cart, result.sections);
     published = true;
    }
   } catch (error) {
    console.error('Free gift: sync failed.', error);
   } finally {
    syncing = false;
    // A published cart rebuilds the drawer, which takes the skeleton with it.
    dropPreview({ announce: !published });
   }
  });
 };

 // Read by assets/custom.js: while a skeleton is showing, the drawer is about to
 // be rebuilt by the gift write, so its interim cross-sell reload would only
 // compete with that write for bandwidth and then be thrown away.
 window.NexbieFreeGift = { hasPreview: () => previewing };

 document.addEventListener('cart:change', (event) => sync(event.detail?.cart));

 // Dispatched by assets/custom.js once its optimistic drawer edits have been
 // reconciled with the server. The drawer flow deliberately never emits
 // cart:change while it is open, so this is the only signal for "the shopper
 // just crossed a tier from inside the drawer".
 document.addEventListener('cart:reconciled', (event) => sync(event.detail?.cart));

 // The click itself, before any request has been sent: assets/custom.js has just
 // painted the new quantity and publishes the subtotal it implies.
 document.addEventListener('cart:optimistic-change', (event) => paintPreview(event.detail?.subtotalCents));

 document.addEventListener(
  'dialog:before-show',
  (event) => {
   if (event.target instanceof Element && event.target.tagName === 'CART-DRAWER') {
    warmGiftImages();
   }
  },
  true
 );

 // theme.js rebuilt the drawer under us (a PDP add, or our own publish): the
 // skeleton lived in the children it replaced, so paint it again as long as the
 // engine is still working towards it.
 document.addEventListener('cart-drawer:refreshed', () => {
  if (syncing) {
   paintPreview(previewSubtotalCents, previewPriorityQuantity);
  }
 });

 document.addEventListener('cart:refresh', async () => {
  try {
   sync(await fetchCart());
  } catch (error) {
   console.error('Free gift: could not read the cart.', error);
  }
 });

 // Seeded from Liquid: settles a cart left inconsistent by a previous session
 // (gift still in the cart after items were removed elsewhere) without paying for
 // an extra /cart.js on every page view.
 sync(config.cart);
})();
